import { formatBirthDateOnly } from "./dateUtils";
import { buildDoctorTaxonomyWriteFields } from "./taxonomyFields";

export const PREFERRED_LANGUAGE_CODE_MAP = {
  tr: 0,
  en: 1,
  de: 2,
  fr: 3,
  ar: 4,
  ru: 5,
  az: 6,
};

export const PREFERRED_LANGUAGE_CODE_TO_I18N = {
  0: "tr",
  1: "en",
  2: "de",
  3: "fr",
  4: "ar",
  5: "ru",
  6: "az",
};

export const educationStatusToApi = (status) => {
  if (status === "Approved" || status === 1) return 1;
  if (status === "Rejected" || status === 2) return 2;
  return 0;
};

const normalizeCountryCode = (countryCode) => {
  if (countryCode == null || countryCode === "") return undefined;
  const raw = countryCode.toString().trim();
  return raw.startsWith("+") ? raw : `+${raw}`;
};

/** PUT profile/contact — yalnızca telefon, ülke kodu, e-posta */
export const buildContactProfilePayload = (values) => ({
  id: values.id,
  phoneNumber: (values.phoneNumber || "").toString().replace(/\s/g, ""),
  countryCode: normalizeCountryCode(values.countryCode),
  email: values.email?.trim(),
});

/** PUT profile/identity — ad, kimlik, diploma. Doktor için onay gerekir. */
export const buildIdentityProfilePayload = (values) => {
  const serial = values.identitySerialNo?.trim?.() ?? values.identitySerialNo;
  const diploma = values.diplomaNo?.trim?.() ?? values.diplomaNo;
  return {
    id: values.id,
    firstName: values.firstName?.trim(),
    lastName: values.lastName?.trim(),
    identityNumber: values.identityNumber?.trim() || undefined,
    identitySerialNo: serial ? String(serial).slice(0, 4) : undefined,
    birthDate: formatBirthDateOnly(values.birthDate) ?? values.birthDate,
    gender: values.gender,
    diplomaNo: diploma ? String(diploma).slice(0, 15) : undefined,
  };
};

export const buildLocalizationProfilePayload = (values) => ({
  id: values.id,
  city: values.city,
  timeZone: values.timeZone,
});

const resolveOptionalText = (primary, fallback) => {
  const value = (primary ?? fallback ?? "").trim();
  return value || null;
};

/** PUT profile/cv — diziler tam replace.
 * Rehber: about/degree boş/null ise mevcut değer korunur.
 * Güvenli olmak için null çıkan alanları payload'dan tamamen çıkarıyoruz.
 */
export const buildCvProfilePayload = ({
  values,
  educations = [],
  experiences = [],
  publications = [],
  languageValues = [],
  about,
  degree,
}) => {
  const resolvedDegree = resolveOptionalText(degree, values.degree ?? values.Degree);
  const resolvedAbout = resolveOptionalText(about, values.about ?? values.About);

  const payload = {
    id: values.id,
    educations: (educations || []).map((e) => ({
      startYear: e.startYear,
      endYear: e.endYear,
      university: e.university,
      department: e.department,
      status: educationStatusToApi(e.status),
      certificateFileName: e.certificateFileName ?? "",
    })),
    experiences: (experiences || []).map((e) => ({
      startYear: e.startYear,
      endYear: e.endYear,
      organization: e.organization,
      department: e.department,
    })),
    publications: (publications || []).map((p) => ({
      year: p.year,
      name: p.publication_name ?? p.name ?? p.publicationName ?? "",
      url: p.url,
    })),
    languageIds: (languageValues || []).map((l) =>
      typeof l === "object" && l?.id != null ? l.id : l
    ),
  };

  if (resolvedDegree != null) payload.degree = resolvedDegree;
  if (resolvedAbout != null) payload.about = resolvedAbout;

  return payload;
};

export const buildSpecialtyProfilePayload = (values, { lockTaxonomy = false } = {}) => {
  const getEffectiveAttrId = (item) => {
    const top =
      item?.specialtyAttributeItemId ??
      item?.specialtyCategoryAttributeItemId;
    const nested =
      item?.specialtyAttributeItem?.id ??
      item?.specialtyCategoryAttributeItem?.id ??
      item?.id;
    return top ?? nested;
  };

  const taxonomy = buildDoctorTaxonomyWriteFields(values);
  const rawItems =
    values.doctorSpecialtyAttributeItems ??
    values.doctorSpecialtyCategoryAttributeItems;

  return {
    id: values.id,
    // Doktor meslek / branş / unvan değiştiremez; null = mevcut değer korunur
    titleId: lockTaxonomy ? null : taxonomy.titleId,
    professionId: lockTaxonomy ? null : taxonomy.professionId,
    primarySpecialtyId: lockTaxonomy ? null : taxonomy.primarySpecialtyId,
    rank: values.rank,
    experienceYear: values.experienceYear?.trim?.() ?? values.experienceYear ?? "",
    doctorSpecialtyAttributeItems: Array.isArray(rawItems)
      ? rawItems
          .map((item) => ({
            specialtyAttributeItemId: getEffectiveAttrId(item),
          }))
          .filter((item) => item.specialtyAttributeItemId != null)
      : [],
  };
};

const DOCTOR_PROFILE_ERROR_KEYS = {
  "DCT-00007": "doctor_profile_blocked",
  "DCT-00008": "doctor_profile_pending_request",
  "DCT-00009": "doctor_profile_approval_required",
  "DCT-00010": "doctor_profile_use_section_endpoints",
  "DCT-00011": "doctor_profile_request_not_allowed",
  "DCT-00017": "doctor_approval_profile_incomplete",
  "DCT-00018": "doctor_profile_taxonomy_locked",
  "USR-00001": "invalid_language_code",
};

export const extractErrorMessage = (errorLike) => {
  if (errorLike == null) return null;
  if (typeof errorLike === "string") return errorLike;
  if (typeof errorLike !== "object") return String(errorLike);

  const messages = errorLike.messages ?? errorLike.Messages;
  if (messages && typeof messages === "object") {
    const first = Object.values(messages)[0];
    if (typeof first === "string") return first;
    if (Array.isArray(first) && first[0]) return String(first[0]);
  }

  const candidates = [
    errorLike.value,
    errorLike.Value,
    errorLike.message,
    errorLike.Message,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate;
  }

  return null;
};

export const getDoctorProfileErrorMessage = (error, t) => {
  const data = error?.data ?? error;
  const messages = data?.error?.messages ?? data?.error?.Messages;
  const errCode =
    data?.error?.key ??
    data?.error?.code ??
    (messages && typeof messages === "object" ? Object.keys(messages)[0] : null);
  const errMsg =
    extractErrorMessage(data?.error) ??
    extractErrorMessage(data) ??
    (typeof error?.message === "string" ? error.message : null);

  if (errCode && DOCTOR_PROFILE_ERROR_KEYS[errCode]) {
    return t(DOCTOR_PROFILE_ERROR_KEYS[errCode]);
  }
  return errMsg || t("profile_update_error");
};

export const applyDoctorDtoToProfileState = (doctorDto, {
  normalizeEducations,
  normalizeExperiences,
  normalizePublications,
} = {}) => {
  if (!doctorDto) return null;

  const next = { ...doctorDto };
  if (normalizeEducations) {
    next.educations = normalizeEducations(
      doctorDto.educations ?? doctorDto.Educations
    );
  }
  if (normalizeExperiences) {
    next.experiences = normalizeExperiences(
      doctorDto.experiences ?? doctorDto.Experiences
    );
  }
  if (normalizePublications) {
    next.publications = normalizePublications(
      doctorDto.publications ?? doctorDto.Publications
    );
  }
  return next;
};
